
// =====================================================================
// Updated TenantSettings Component with i18n
// Description: Settings component with internationalization support
// Location: src/components/TenantSettings.jsx
// =====================================================================
// 
// IMPORTANT: This file REPLACES the existing TenantSettings.jsx
// It adds i18n support while preserving ALL existing functionality
// 
// Changes made:
// 1. Added useI18n hook import and usage
// 2. Updated Language component to use i18n context
// 3. Language now saves to database via API
// 4. Added translation support (can be applied to other sections later)
// =====================================================================

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Settings as SettingsIcon,
  Building2,
  Shield,
  Palette,
  Eye,
  Bell,
  Globe,
  Upload,
  FileText,
  Sun,
  Moon,
  Monitor,
  RefreshCw,
} from "lucide-react";
import AppFooter from "./AppFooter";
import { FaTimes, FaSave, FaArrowLeft, FaHome } from "react-icons/fa";
import PushNotificationSettings from './PushNotificationSettings';
import { useI18n } from '../context/i18nContext'; // NEW: Import i18n hook

const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

export default function TenantSettings() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const { t, currentLanguage, changeLanguage, supportedLanguages } = useI18n(); // NEW: Use i18n hook

  // -------- state
  const [tenantData, setTenantData] = useState({
    id: null,
    ownerid: null,
    companyname: "",
    requiremfa: false,
    domain: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    logo: null,
    waiver: null,
    active: true,
  });

  const [pwaSettings, setPwaSettings] = useState({
    theme: "system", // 'light' | 'dark' | 'system'
    reducedMotion: false,
    language: currentLanguage || "en-US", // UPDATED: Use current language from i18n
    notifications: { enabled: false, marketing: false, security: true, updates: true },
  });

  const [activeTab, setActiveTab] = useState("company");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [showInCollabDirectory, setShowInCollabDirectory] = useState(true);

  // -------- helpers
  const getAssetUrl = (p) => {
    if (!p) return "";
    if (p.startsWith("http://") || p.startsWith("https://")) return p;
    return `${baseUrl}${p}`;
  };

  const tabs = [
    { id: "company", label: "Company", icon: Building2 },
    { id: "security", label: "Security", icon: Shield },
    { id: "appearance", label: "Appearance", icon: Palette },
    { id: "accessibility", label: "Accessibility", icon: Eye },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "language", label: "Language", icon: Globe },
    { id: "collaboration", label: "Collaboration", icon: Eye },
  ];

  // -------- effects
  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        // Load tenant data from API using tenant ID from URL
        const res = await axios.get(`${baseUrl}/api/tenants/${tenant}/settings`, {
          withCredentials: true,
        });
        
        console.log('Tenant settings response:', res.data);
        
        if (res.data?.success && res.data?.tenant) {
          setTenantData(res.data.tenant);
        } else {
          setStatus("Could not load tenant data");
          console.error("Invalid response format:", res.data);
        }

        // Load PWA settings from localStorage
        const saved = localStorage.getItem("pwaSettings");
        if (saved) {
          try {
            setPwaSettings(JSON.parse(saved));
          } catch (e) {
            console.error("Error parsing saved PWA settings:", e);
          }
        }

        // Detect system preferences
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
          setPwaSettings((p) => ({ ...p, reducedMotion: true }));
        }
        if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
          document.documentElement.classList.add("dark");
        }
      } catch (e) {
        console.error("Error loading settings:", e);
        const errorMsg = e.response?.data?.message || e.message || "Error loading settings";
        setStatus(`Error: ${errorMsg}`);
      } finally {
        setBusy(false);
      }
    })();
  }, [tenant]);

  // UPDATED: Sync pwaSettings.language with currentLanguage from i18n
  useEffect(() => {
    if (currentLanguage && currentLanguage !== pwaSettings.language) {
      setPwaSettings(prev => ({ ...prev, language: currentLanguage }));
    }
  }, [currentLanguage]);

  // Load Collaboration setting
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${baseUrl}/api/collab/${tenant}/settings`, {
          withCredentials: true,
        });
        setShowInCollabDirectory(!!r.data?.showInCollabDirectory);
      } catch (e) {
        // Silent fail - not critical
        console.error("Error loading collab settings:", e);
      }
    })();
  }, [tenant]);

  // -------- handlers
  const handleTenantChange = (field, value) =>
    setTenantData((p) => ({ ...p, [field]: value }));

  const handlePwaSettingChange = (field, value) => {
    const next = { ...pwaSettings, [field]: value };
    setPwaSettings(next);
    try {
      localStorage.setItem("pwaSettings", JSON.stringify(next));
    } catch (e) {
      console.error("Error saving PWA settings:", e);
    }
  };

  const handleNotificationToggle = (k, v) =>
    setPwaSettings((p) => ({
      ...p,
      notifications: { ...p.notifications, [k]: v },
    }));

  const handleThemeChange = (theme) => {
    handlePwaSettingChange("theme", theme);
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else if (theme === "light") {
      document.documentElement.classList.remove("dark");
    } else {
      // System preference
      const isDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches;
      document.documentElement.classList.toggle("dark", !!isDark);
    }
  };

  // NEW: Language change handler using i18n context
  const handleLanguageChange = async (languageCode) => {
    setBusy(true);
    setStatus(t('settings.saving') || "Saving...");
    
    try {
      // Change language using i18n context (this saves to database automatically)
      const success = await changeLanguage(languageCode);
      
      if (success) {
        // Update local pwaSettings for consistency
        handlePwaSettingChange("language", languageCode);
        setStatus(t('settings.languageUpdated') || "Language updated successfully");
        
        // Optional: Show message about page refresh
        setTimeout(() => {
          setStatus(t('settings.languageDescription') || "Language changes take effect after refreshing the app");
        }, 2000);
      } else {
        setStatus(t('settings.languageError') || "Error updating language preference");
      }
    } catch (error) {
      console.error("Error changing language:", error);
      setStatus(t('settings.languageError') || "Error updating language preference");
    } finally {
      setBusy(false);
      setTimeout(() => setStatus(""), 5000);
    }
  };

  const uploadFileToServer = async (field, file, type) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type); // 'logo' | 'waiver'
    
    try {
      setBusy(true);
      const res = await axios.post(
        `${baseUrl}/api/tenants/${tenant}/media`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      
      if (res.data?.success) {
        handleTenantChange(field, res.data.path);
        setStatus(`${type === "logo" ? "Logo" : "Waiver"} uploaded`);
      } else {
        setStatus("Upload failed");
      }
    } catch (e) {
      console.error("Upload error:", e);
      setStatus("Upload error");
    } finally {
      setBusy(false);
      setTimeout(() => setStatus(""), 2500);
    }
  };

  const handleFileUpload = (field, file) => file && uploadFileToServer(field, file, field);

  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      setStatus("Notifications not supported");
      setTimeout(() => setStatus(""), 2000);
      return;
    }

    try {
      const perm = await Notification.requestPermission();
      if (perm === "granted") {
        handleNotificationToggle("enabled", true);
        setStatus("Notifications enabled");
      } else {
        setStatus("Notification permission denied");
      }
      setTimeout(() => setStatus(""), 2000);
    } catch (e) {
      console.error("Notification permission error:", e);
      setStatus("Error requesting permission");
      setTimeout(() => setStatus(""), 2000);
    }
  };

  const saveAll = async () => {
    if (!tenantData.companyname || !tenantData.email) {
      setStatus("Company name and email are required");
      setTimeout(() => setStatus(""), 3000);
      return;
    }

    setBusy(true);
    setStatus("Saving…");

    try {
      await axios.put(
        `${baseUrl}/api/tenants/${tenant}/settings`,
        tenantData,
        {
          withCredentials: true,
        }
      );
      setStatus("Settings saved");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      console.error("Save error:", e);
      setStatus(e.response?.data?.message || "Save error");
      setTimeout(() => setStatus(""), 3000);
    } finally {
      setBusy(false);
    }
  };

  const goDashboard = () => navigate(`/${tenant}/dashboard`);

  // -------- components
  const Company = () => (
    <div>
      <div className="form-group">
        <label htmlFor="companyname">Company Name</label>
        <input
          id="companyname"
          className="form-control"
          value={tenantData.companyname || ""}
          onChange={(e) => handleTenantChange("companyname", e.target.value)}
        />
      </div>

      <div className="form-group">
        <label htmlFor="email">Company Email</label>
        <input
          id="email"
          type="email"
          className="form-control"
          value={tenantData.email || ""}
          onChange={(e) => handleTenantChange("email", e.target.value)}
        />
      </div>

      <div className="form-group">
        <label htmlFor="phone">Phone</label>
        <input
          id="phone"
          type="tel"
          className="form-control"
          value={tenantData.phone || ""}
          onChange={(e) => handleTenantChange("phone", e.target.value)}
        />
      </div>

      <div className="form-group">
        <label htmlFor="address1">Address Line 1</label>
        <input
          id="address1"
          className="form-control"
          value={tenantData.address1 || ""}
          onChange={(e) => handleTenantChange("address1", e.target.value)}
        />
      </div>

      <div className="form-group">
        <label htmlFor="address2">Address Line 2</label>
        <input
          id="address2"
          className="form-control"
          value={tenantData.address2 || ""}
          onChange={(e) => handleTenantChange("address2", e.target.value)}
        />
      </div>

      <div style={{ display: "flex", gap: "10px" }}>
        <div className="form-group" style={{ flex: 1 }}>
          <label htmlFor="city">City</label>
          <input
            id="city"
            className="form-control"
            value={tenantData.city || ""}
            onChange={(e) => handleTenantChange("city", e.target.value)}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label htmlFor="state">State</label>
          <input
            id="state"
            className="form-control"
            value={tenantData.state || ""}
            onChange={(e) => handleTenantChange("state", e.target.value)}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label htmlFor="zip">ZIP</label>
          <input
            id="zip"
            className="form-control"
            value={tenantData.zip || ""}
            onChange={(e) => handleTenantChange("zip", e.target.value)}
          />
        </div>
      </div>

      <div className="form-group">
        <label>
          <Upload style={{ width: "16px", marginRight: "8px" }} />
          Company Logo
        </label>
        {tenantData.logo && (
          <div style={{ marginBottom: "10px" }}>
            <img
              src={getAssetUrl(tenantData.logo)}
              alt="Company Logo"
              style={{ maxWidth: "200px", maxHeight: "100px", objectFit: "contain" }}
            />
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          onChange={(e) => handleFileUpload("logo", e.target.files[0])}
          className="form-control"
        />
      </div>

      <div className="form-group">
        <label>
          <FileText style={{ width: "16px", marginRight: "8px" }} />
          Waiver Document
        </label>
        {tenantData.waiver && (
          <div style={{ marginBottom: "10px", fontSize: "14px", color: "#666" }}>
            Current: <a href={getAssetUrl(tenantData.waiver)} target="_blank" rel="noopener noreferrer">View Waiver</a>
          </div>
        )}
        <input
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={(e) => handleFileUpload("waiver", e.target.files[0])}
          className="form-control"
        />
      </div>
    </div>
  );

  const Security = () => (
    <div>
      <div className="form-group">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={tenantData.requiremfa}
            onChange={(e) => handleTenantChange("requiremfa", e.target.checked)}
          />
          <span>Require Multi-Factor Authentication</span>
        </label>
        <p style={{ fontSize: "14px", color: "#666", marginTop: "8px" }}>
          Require all users to set up 2FA for enhanced security
        </p>
      </div>
    </div>
  );

  const Appearance = () => (
    <div>
      <div className="form-group">
        <label>Theme</label>
        <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
          {[
            { value: "light", label: "Light", icon: Sun },
            { value: "dark", label: "Dark", icon: Moon },
            { value: "system", label: "System", icon: Monitor },
          ].map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => handleThemeChange(value)}
              className={`btn ${
                pwaSettings.theme === value ? "btn-primary" : "btn-outline-primary"
              }`}
              style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
            >
              <Icon style={{ width: "16px", height: "16px" }} />
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const Accessibility = () => (
    <div>
      <div className="form-group">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={pwaSettings.reducedMotion}
            onChange={(e) => handlePwaSettingChange("reducedMotion", e.target.checked)}
          />
          <span>Reduce motion and animations</span>
        </label>
        <p style={{ fontSize: "14px", color: "#666", marginTop: "8px" }}>
          Minimizes animations for users sensitive to motion
        </p>
      </div>

      <div style={{ marginTop: "20px" }}>
        <h4 style={{ fontWeight: "600", marginBottom: "10px" }}>Built-in Features</h4>
        <ul style={{ listStyle: "disc", paddingLeft: "20px", fontSize: "14px", color: "#666" }}>
          <li>High contrast support</li>
          <li>Keyboard navigation</li>
          <li>Screen reader compatibility</li>
          <li>Touch/stylus input support</li>
        </ul>
      </div>
    </div>
  );

  const Notifications = () => (
    <div>
      {/* Push Notification Settings Component */}
      <PushNotificationSettings 
        showTestButton={true}
        onStatusChange={(status) => {
          console.log('Notification status changed:', status);
          // Update local state if needed
          if (status.enabled) {
            handleNotificationToggle("enabled", true);
          }
        }}
      />

      {/* Additional notification preferences */}
      {pwaSettings.notifications.enabled && (
        <div style={{ marginTop: "30px" }}>
          <h4 style={{ fontWeight: "600", marginBottom: "15px", color: "#01057a" }}>
            Notification Preferences
          </h4>
          <div style={{ paddingLeft: "20px", borderLeft: "3px solid #01057a" }}>
            {[
              { key: "security", label: "Security alerts", description: "Get notified about security-related events" },
              { key: "updates", label: "App updates", description: "Receive notifications about new features and updates" },
              { key: "marketing", label: "Marketing", description: "Promotional offers and announcements" },
            ].map((o) => (
              <div key={o.key} className="form-group" style={{ marginBottom: "15px" }}>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={pwaSettings.notifications[o.key]}
                    onChange={(e) => handleNotificationToggle(o.key, e.target.checked)}
                  />
                  <div>
                    <div style={{ fontWeight: "500" }}>{o.label}</div>
                    <div style={{ fontSize: "12px", color: "#666" }}>{o.description}</div>
                  </div>
                </label>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const Collaboration = () => (
    <div>
      <div className="form-group">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={showInCollabDirectory}
            onChange={async (e) => {
              const next = e.target.checked;
              setShowInCollabDirectory(next);
              try {
                await axios.patch(
                  `${baseUrl}/api/collab/${tenant}/settings`,
                  { showInCollabDirectory: next },
                  { withCredentials: true }
                );
                setStatus("Collaboration setting updated");
                setTimeout(() => setStatus(""), 2000);
              } catch (err) {
                console.error(err);
                setStatus("Error updating setting");
                setTimeout(() => setStatus(""), 2000);
                setShowInCollabDirectory(!next);
              }
            }}
          />
          <span>Show in collaboration directory</span>
        </label>
        <p style={{ fontSize: "14px", color: "#666", marginTop: "8px" }}>
          Allow other tenants to discover and collaborate with you
        </p>
      </div>
    </div>
  );

  // UPDATED: Language component with i18n integration
  const Language = () => (
    <div>
      <div className="form-group">
        <label htmlFor="language">
          {t('settings.languageLabel') || 'Language'}
        </label>
        <select
          id="language"
          className="form-control"
          value={currentLanguage}
          onChange={(e) => handleLanguageChange(e.target.value)}
          disabled={busy}
        >
          {supportedLanguages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.nativeName}
            </option>
          ))}
        </select>
      </div>
      <p style={{ fontSize: "14px", color: "#666", marginTop: "8px" }}>
        {t('settings.languageDescription') || 'Choose your preferred language. Changes take effect after refreshing the app.'}
      </p>
      
      {/* Optional: Add a refresh button */}
      <div style={{ marginTop: "15px" }}>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => window.location.reload()}
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <RefreshCw style={{ width: "14px", height: "14px" }} />
          Refresh App to Apply Changes
        </button>
      </div>
    </div>
  );

  // -------- render
  if (busy && !tenantData.id) {
    return <div className="container py-4"><p>Loading settings...</p></div>;
  }

  return (
    <div className="container">
      <div style={{ textAlign: "center", marginBottom: "20px", position: "relative" }}>
        <h2 className="hdr hdr--primary hdr--lg">Settings</h2>
        {busy && (
          <RefreshCw 
            style={{ 
              width: "20px", 
              height: "20px", 
              animation: "spin 1s linear infinite",
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)"
            }} 
          />
        )}
      </div>

      {status && (
        <div
          style={{
            marginBottom: "20px",
            padding: "12px",
            borderRadius: "8px",
            backgroundColor: status.includes("Error") ? "#fee2e2" : "#d1fae5",
            color: status.includes("Error") ? "#991b1b" : "#065f46",
            fontSize: "14px",
          }}
        >
          {status}
        </div>
      )}

      {/* Tab grid */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: "repeat(2, 1fr)", 
        gap: "8px", 
        marginBottom: "20px" 
      }}>
        {tabs.map(({ id, label, icon: Icon }) => {
          const active = activeTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={`btn btn-sm ${active ? "btn-primary" : "btn-outline-primary"}`}
              style={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                gap: "4px",
                padding: "8px 4px",
                fontSize: "12px",
                minHeight: "36px"
              }}
            >
              <Icon style={{ width: "14px", height: "14px", flexShrink: 0 }} />
              <span style={{ lineHeight: "1.2" }}>{label}</span>
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div style={{ marginBottom: "80px" }}>
        {activeTab === "company" && <Company />}
        {activeTab === "security" && <Security />}
        {activeTab === "appearance" && <Appearance />}
        {activeTab === "accessibility" && <Accessibility />}
        {activeTab === "notifications" && <Notifications />}
        {activeTab === "language" && <Language />}
        {activeTab === "collaboration" && <Collaboration />}
      </div>

      {/* Footer actions */}
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: () => navigate(-1),
          },
          { icon: <FaHome />, label: "Home", onClick: goDashboard },
          {
            icon: busy ? <RefreshCw className="animate-spin" /> : <FaSave />,
            label: busy ? "Saving…" : "Save",
            onClick: saveAll,
            type: "button",
            disabled: busy,
          },
        ]}
      />
    </div>
  );
}